
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { supabase } from "@/integrations/supabase/client"
import { Tables } from "@/integrations/supabase/types"
import { toast } from "sonner"

type Timesheet = Tables<"timesheets">
type Profile = Tables<"profiles">

type TimesheetWithEmployee = Timesheet & {
  employee: Profile | null
  approved_by_user: Profile | null
}

export const useTimesheets = () => {
  // Check if QueryClient is available
  try {
    const queryClient = useQueryClient()
  } catch {
    // Return mock data when QueryClient is not available
    return {
      data: [],
      isLoading: false,
      error: null
    }
  }
  
  return useQuery({
    queryKey: ["timesheets"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("timesheets")
        .select(`
          *,
          employee:profiles!timesheets_employee_id_fkey(*),
          approved_by_user:profiles!timesheets_approved_by_fkey(*)
        `)
        .order("work_date", { ascending: false })

      if (error) throw error
      return data as TimesheetWithEmployee[]
    }
  })
}

export const useCreateTimesheet = () => {
  // Check if QueryClient is available
  let queryClient
  try {
    queryClient = useQueryClient()
  } catch {
    // Return no-op mutation when QueryClient is not available
    return {
      mutate: () => {},
      mutateAsync: async () => ({}),
      isPending: false,
      isError: false,
      error: null
    }
  }

  return useMutation({
    mutationFn: async (data: Omit<Timesheet, "id" | "created_at" | "updated_at">) => {
      const { data: timesheet, error } = await supabase
        .from("timesheets")
        .insert(data)
        .select()
        .single()

      if (error) throw error
      return timesheet
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["timesheets"] })
      toast.success("Ponto registrado com sucesso!")
    },
    onError: (error) => {
      console.error("Erro ao registrar ponto:", error)
      toast.error("Erro ao registrar ponto")
    }
  })
}

export const useUpdateTimesheetApproval = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async ({ 
      timesheetId, 
      status, 
      justificativa 
    }: { 
      timesheetId: string
      status: string
      justificativa?: string 
    }) => {
      const { data, error } = await supabase
        .from("timesheets")
        .update({
          status,
          justification: justificativa,
          updated_at: new Date().toISOString()
        })
        .eq("id", timesheetId)
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["timesheets"] })
      toast.success("Folha de ponto atualizada com sucesso!")
    },
    onError: (error) => {
      console.error("Erro ao atualizar folha de ponto:", error)
      toast.error("Erro ao atualizar folha de ponto")
    }
  })
}
